import React from 'react';
import { companyValues, groupCompanies } from '../data/mock';
import { Target, Eye, Award } from 'lucide-react';

const About = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>
              Our Journey & Purpose
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '32px'
            }}>
              Founded in 2017, Care Pharmacy was born to make quality healthcare accessible and affordable for every family. From one store to 190+ outlets, we continue to expand a premium, one-roof pharmacy experience backed by strong supply, automation, and customer-first service.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '48px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <Target size={40} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-2" style={{ marginBottom: '16px' }}>Our Mission</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Caring health & wealth — by delivering quality medicines, reliable guidance and everyday savings to families across India.
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Eye size={40} style={{ color: 'var(--brand-green)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-2" style={{ marginBottom: '16px' }}>Our Vision</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                India's most trusted one-roof healthcare retail platform, powered by technology, transparency and a passionate team.
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Award size={40} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-2" style={{ marginBottom: '16px' }}>Our Commitment</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Building a healthcare ecosystem that prioritizes customer wellbeing, ethical practices, and sustainable growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Our Core Values
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {companyValues.map((value) => (
              <div key={value.id} className="hover-lift" style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                  {value.title}
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Group Companies */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Our Group Companies
          </h2>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            marginBottom: '60px',
            textAlign: 'center',
            maxWidth: '700px',
            margin: '0 auto 60px'
          }}>
            Care Pharmacy is part of a diversified healthcare group serving the complete pharmaceutical value chain.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px'
          }}>
            {groupCompanies.map((company) => (
              <div key={company.id} className="hover-lift" style={{
                padding: '32px',
                background: 'var(--bg-primary)',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: 'var(--bg-secondary)',
                  marginBottom: '16px',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: 'var(--text-primary)'
                }}>
                  {company.focus}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                  {company.name}
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  {company.description}
                </p>
                {company.website && (
                  <a
                    href={`https://${company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="body-small"
                    style={{
                      color: 'var(--brand-blue)',
                      textDecoration: 'underline'
                    }}
                  >
                    {company.website}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team & Culture */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            Our People & Culture
          </h2>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            marginBottom: '60px',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 60px'
          }}>
            Behind every Care Pharmacy store is a dedicated team committed to serving our communities with professionalism, empathy, and excellence.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {['/images/events/event_10.jpg', '/images/events/event_22.jpg', '/images/events/event_45.jpg', '/images/events/event_23.jpg'].map((img, index) => (
              <div key={index} className="hover-lift" style={{
                overflow: 'hidden',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}>
                <img 
                  src={img} 
                  alt={`Care Pharmacy Team ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '280px',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Our Growth Story
          </h2>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div style={{
              borderLeft: '2px solid var(--border-light)',
              paddingLeft: '32px'
            }}>
              {[
                { year: '2017', title: 'Foundation', description: 'Care Pharmacy launched with a vision to democratize quality healthcare in India.' },
                { year: '2019', title: 'Expansion Begins', description: 'Reached 50+ stores across Gujarat with strong customer trust and repeat business.' },
                { year: '2021', title: 'Pan-India Presence', description: 'Expanded operations to 10+ states with focus on tier-2 and tier-3 cities.' },
                { year: '2023', title: 'Group Formation', description: 'Established One Deal, Pickora, and CurePath as specialized business verticals.' },
                { year: '2024', title: 'Technology Integration', description: 'Deployed automation, digital receipts, and omnichannel ordering at scale.' },
                { year: '2025', title: '190+ Stores', description: "India's fast-emerging pharmacy chain with nationwide presence and strong franchise network." }
              ].map((milestone, index) => (
                <div key={index} style={{
                  marginBottom: index < 5 ? '48px' : 0,
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    left: '-39px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: 'var(--brand-blue)',
                    border: '3px solid var(--bg-primary)'
                  }} />
                  <div className="body-small" style={{
                    color: 'var(--brand-green)',
                    fontWeight: '500',
                    marginBottom: '8px'
                  }}>
                    {milestone.year}
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '8px' }}>
                    {milestone.title}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {milestone.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;